import {
  userInfoStore,
  roundInfoStore,
  gameConfigStore,
  applicationStore,
  popupStore,
  loadingStore,
  DefaultQueryParams,
  GameModes,
  freeBetStore,
  notificationStore,
  parseQuery,
  tableBetHistoryStore,
} from '@pascal/pascal-core';
import { gameStore } from '../store/gameStore';
import { doBetStore } from '../store/doBetStore';
import { cardsStore } from '../store/cardsStore';
import { winningStore } from '../store/winningStore';
import { gameLiveConnection } from './GameConnection';
import { betResponseCodeByName } from '../enums/betResponseCode';
import { autoBetStore } from '../store/autoBetStore';
import { soundService } from './SoundService';
import { INFINITY_AUTO_BET } from '../enums/constants';
import { liveBetsDataStore } from '../store/liveBetsDataStore';

class TurboGameService {
  onGetInitialStateResult({
    rate,
    limits,
    balance,
    liveBets,
    precision,
    currencyCode,
    isTestPlatform,
    underMaintenance,
  }) {
    this.handleResetDataInitial();
    winningStore.setWinCombination(null);
    const {
      [DefaultQueryParams.CurrencySymbol]: currencySymbol,
      [DefaultQueryParams.UserId]: userId,
      [DefaultQueryParams.Mode]: mode,
    } = parseQuery(window.location.search);
    userInfoStore.setUserInfo({
      rate,
      userId,
      balance,
      precision,
      currencyCode,
      currencySymbol,
    });
    popupStore.setReloadFrameForUnderMaintenanceOnRoundStart(true);
    if (underMaintenance) {
      popupStore.startUnderMaintenanceFunctional(underMaintenance);
    }
    liveBetsDataStore.setLiveBets(liveBets);
    gameConfigStore.setLimits(limits);
    applicationStore.setIsDemo(mode === GameModes.Fun || isTestPlatform);
    applicationStore.setIsTestPlatform(isTestPlatform);
    doBetStore.setQuickBets(limits?.quickBets);
    if (!freeBetStore.activeFreeBet.id) {
      doBetStore.setSelectedAmount(
        limits?.quickBets[1] > balance ? limits?.quickBets[0] : limits?.quickBets[1]
      );
    }

    loadingStore.updateLoading({ isGameInitialLoaded: true });
    gameStore.setGameTypeId();

    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();
  }

  handleDoBetResult({ finishedRound, betResult: { winAmount, balance, roundId, freeBetId } }) {
    if (!freeBetId) {
      userInfoStore.setBalance(balance);
    }
    roundInfoStore.setRoundId(roundId);
    cardsStore.closeCards();
    winningStore.setWinCombination(null);
    setTimeout(() => {
      cardsStore.setWithOutWinCards(finishedRound.roundData.cards);
    }, 300);
    this.handleRoundWinning(
      winAmount,
      finishedRound.roundData.cards,
      finishedRound.roundData.winCombination,
      freeBetId
    );
  }

  handleDoBetResultValidation({ betResult: { responseCode } }) {
    notificationStore.setNotificationType(responseCode);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
    if (autoBetStore.isPlay) {
      autoBetStore.resetAutoBet();
    }
    gameStore.setIsBetsClosed(false);
  }

  sendBet() {
    cardsStore.removeWinInCards();
    if (
      !freeBetStore.activeFreeBet.id &&
      (doBetStore.selectedAmount > userInfoStore.balance || !userInfoStore.balance)
    ) {
      if (autoBetStore.isPlay) {
        autoBetStore.resetAutoBet();
      }
      notificationStore.setNotificationType(betResponseCodeByName.NotEnoughBalance);
      setTimeout(() => notificationStore.setNotificationType(null), 1000);
      autoBetStore.resetAutoBet();
      gameStore.setIsBetsClosed(false);
    } else {
      gameStore.setIsBetsClosed(true);
      if (autoBetStore.autoBetCount) {
        if (!autoBetStore.isPlay) {
          autoBetStore.setIsPlay();
        }
      }
      const { [DefaultQueryParams.GameTypeId]: gameTypeId } = parseQuery(window.location.search);
      if (applicationStore.isDemo) {
        gameLiveConnection.doDemoBet(doBetStore.selectedAmount);
      } else {
        gameLiveConnection.doBet(
          freeBetStore.activeFreeBet.id
            ? {
                amount: doBetStore.selectedAmount,
                gameTypeId,
                freeBetId: freeBetStore.activeFreeBet.id,
              }
            : {
                amount: doBetStore.selectedAmount,
                gameTypeId,
              }
        );
      }
    }
  }

  handleDoDemoBetResult({ amount, winAmount, result: { cards, winCombination } }) {
    userInfoStore.setDemoBalance(userInfoStore.balance - amount);
    cardsStore.closeCards();
    winningStore.setWinCombination(null);
    setTimeout(() => {
      cardsStore.setWithOutWinCards(cards);
    }, 300);
    setTimeout(() => {
      this.handleRoundWinning(winAmount, cards, winCombination);
    }, 500);
  }

  handleLiveBetsResult(liveBet) {
    setTimeout(() => {
      liveBetsDataStore.updateLiveBets(liveBet);
    }, 600);
  }

  handleTurboBetAccepted() {}

  handleResultReport() {}

  handleRoundWinning(winAmount, cards, winCombination, freeBetId) {
    setTimeout(() => {
      if (applicationStore.isDemo) {
        userInfoStore.setDemoBalance(userInfoStore.balance + winAmount);
      } else {
        gameLiveConnection.getBalance();
      }
      freeBetStore.handleFreeBetEndedData();
    }, 600);
    if (winAmount) {
      setTimeout(() => {
        if (freeBetId) {
          freeBetStore.freeBetTotalWinCalculator({ freeBetId, winAmount });
        }
        cardsStore.setWinInCards(cards);
        soundService.playWin();
        winningStore.setWinningAmount(winAmount);
        winningStore.setWinCombination(winCombination);
        setTimeout(() => {
          this.handleAutoBet();
          this.handleResetEndedRoundData();
          winningStore.setLastWin(winAmount);
        }, 500);
      }, 500);
    } else {
      setTimeout(() => {
        this.handleAutoBet();
      }, 500);
      setTimeout(() => {
        this.handleResetEndedRoundData();
      }, 500);
    }
  }

  handleAutoBet() {
    if (
      (autoBetStore.autoBetCount - 1 >= 1 || autoBetStore.autoBetCount === INFINITY_AUTO_BET) &&
      autoBetStore.isPlay &&
      (!freeBetStore.activeFreeBet.id ||
        (freeBetStore.activeFreeBet.id && freeBetStore.activeFreeBet.remainingCount))
    ) {
      if (autoBetStore.autoBetCount !== INFINITY_AUTO_BET) {
        autoBetStore.decrementAutoBetCount();
      }
      this.sendBet();
    } else {
      autoBetStore.resetAutoBet();
      gameStore.setIsBetsClosed(false);
    }
  }

  handleResetEndedRoundData() {
    if (!applicationStore.isDemo) {
      tableBetHistoryStore.handleBetHistoryUpdate();
    }
    roundInfoStore.setRoundId(null);
    winningStore.clearWinInfo();
  }

  handleResetDataInitial() {
    winningStore.setLastWin(0);
    winningStore.clearWinInfo();
    cardsStore.setTableCards([]);
    gameStore.setIsBetsClosed(false);
    autoBetStore.resetAutoBet();
  }
}

export const turboGameService = new TurboGameService();
